import React, { Fragment } from 'react'
import PropTypes from 'prop-types'

import S from './Legal.styled'
import configLinks from '../../../config/footer/legal'
import { useFooterContext } from '../FooterContext'
import Link from '../../../modules/link/Link'
import { track } from '../../../scripts/segment'
import { TabNavigation } from '../../TabNavigation'
import { useSessionContext } from '../../../api/crapi/SessionContext'
import languages from '../../../../languages'
import { addIntlKey } from '../../../utils'
import { useEnvDomain } from '../../../hooks'

const { genderEqualityUrl, sonyDnsUrl, adChoicesUrl, ukMsaUrl } = configLinks

const Legal = ({
  defaultLinkTheme = 'marketing',
  addLegalLinks = [],
  legalLinkText = {},
  showPrivacyLink = true,
  showTermsLink = true,
  showBlogLink = true,
  showConsentModal = true,
  showFrGenderEqualityLink = false,
  showUkMsaLink = true,
  enableTracking = false,
  pageOrScreen = '',
  lang = 'en',
  disableAllTargetBlank = false,
}) => {
  const domain = useEnvDomain()

  const isLinkDisplayed = {
    privacy: showPrivacyLink,
    terms: showTermsLink,
    blog: showBlogLink,
  }

  const defaultLinks = configLinks[defaultLinkTheme] || []
  const defaultLinksDisplayed = defaultLinks.map((link) => {
    return { ...link, isDisplayed: isLinkDisplayed[link.key] }
  })

  const addLegalLinksDisplayed = addLegalLinks.map((link) => {
    return { ...link, isDisplayed: true }
  })

  const links = [...addLegalLinksDisplayed, ...defaultLinksDisplayed]
  if (!links) return null

  const intlFooter = languages[lang]
    ? languages[lang].footer
    : languages.en.footer

  const {
    blog: defaultBlogText,
    privacy: defaultPrivacyText,
    terms: defaultTermsText,
    cookie: defaultCookieText,
  } = intlFooter

  const {
    blog: customBlogText,
    privacy: customPrivacyText,
    terms: customTermsText,
    cookie: customCookieText,
  } = legalLinkText

  const blog = customBlogText || defaultBlogText
  const privacy = customPrivacyText || defaultPrivacyText
  const terms = customTermsText || defaultTermsText
  const cookie = defaultCookieText || customCookieText

  const intlLinks = addIntlKey({ blog, privacy, terms }, links)

  const session = useSessionContext()
  const countryCode = session ? session.countryCode : ''

  const onClick = (text, eventTitle) => {
    const actionDetail = {
      textOfButtonOrLink: text,
      pageOrScreen: pageOrScreen,
      position: 'bottom',
      referrer: window.location.href,
    }

    if (enableTracking && eventTitle) {
      track(eventTitle, actionDetail)
    }
  }

  const onConsentClick = (event) => {
    event.preventDefault()

    if (window.Optanon) {
      return window.Optanon.ToggleInfoDisplay()
    }
  }

  return (
    <S.Legal className="legal-list">
      {intlLinks.map(
        ({
          key,
          text,
          url,
          isCrUrl,
          targetBlank,
          eventTitle,
          intlText,
          isDisplayed,
          linkComponent: RouteLink,
        }) => {
          const linkText = intlText || text

          return (
            <Fragment key={key}>
              {isDisplayed && (
                <>
                  {RouteLink ? (
                    <li className="legal-item">
                      <RouteLink
                        to={url}
                        className="legal-link"
                        onClick={() => onClick(linkText, eventTitle)}
                      >
                        {linkText}
                      </RouteLink>
                    </li>
                  ) : (
                    <li className="legal-item">
                      <Link
                        to={isCrUrl ? `${domain}${url}` : url}
                        className="legal-link"
                        blank={
                          disableAllTargetBlank
                            ? false
                            : targetBlank
                              ? true
                              : false
                        }
                        onClick={() => onClick(linkText, eventTitle)}
                      >
                        {linkText}
                      </Link>
                    </li>
                  )}
                </>
              )}
            </Fragment>
          )
        },
      )}

      {lang === 'en' && (
        <>
          <li className="legal-item">
            <Link
              to={adChoicesUrl}
              className="legal-link"
              blank={disableAllTargetBlank ? false : true}
            >
              AdChoices
            </Link>
          </li>
          <li className="legal-item">
            <Link
              to={sonyDnsUrl}
              className="legal-link ccpa"
              blank={disableAllTargetBlank ? false : true}
            >
              Do Not Sell or Share My Personal Information
            </Link>
          </li>
        </>
      )}

      {showConsentModal && (
        <li className="legal-item legal-dns">
          <TabNavigation onClick={onConsentClick} onEnterPress={onConsentClick}>
            <span>{cookie}</span>
          </TabNavigation>
        </li>
      )}

      {countryCode === 'FR' && showFrGenderEqualityLink && (
        <li className="legal-item">
          <Link
            to={genderEqualityUrl}
            className="legal-link"
            blank={disableAllTargetBlank ? false : true}
          >
            Index 2025 : 72/100
          </Link>
        </li>
      )}

      {countryCode === 'GB' && showUkMsaLink && (
        <li className="legal-item">
          <Link
            to={ukMsaUrl}
            className="legal-link"
            blank={disableAllTargetBlank ? false : true}
          >
            Modern Slavery Transparency Statement
          </Link>
        </li>
      )}
    </S.Legal>
  )
}

Legal.propTypes = {
  defaultLinkTheme: PropTypes.string,
  addLegalLinks: PropTypes.array,
  legalLinkText: PropTypes.object,
  showPrivacyLink: PropTypes.bool,
  showTermsLink: PropTypes.bool,
  showBlogLink: PropTypes.bool,
  showConsentModal: PropTypes.bool,
  showFrGenderEqualityLink: PropTypes.bool,
  showUkMsaLink: PropTypes.bool,
  enableTracking: PropTypes.bool,
  pageOrScreen: PropTypes.string,
  lang: PropTypes.string,
}

export default Legal
